<template>
  <div>
    <!-- Authorizing mints the PaymentIntent server-side and can take a while (or
         time out at the payment gateway). Without this the final step is simply
         blank for as long as it hangs, which reads as a broken page. -->
    <div v-if="isPreparingPayment" class="payment-preparing text-left">
      <div class="boires-page-loader payment-preparing__spinner" />
      <span class="payment-preparing__label">{{ $t('checkout.preparingPayment') }}</span>
    </div>
    <div v-show="isPaymentVisible">
      <form id="payment-form" @submit="handleSubmit">
        <div id="payment-element">
          <!--Stripe.js injects the Payment Element-->
        </div>
        <button id="submit" :disabled="isLoading">
          <div id="spinner" class="spinner hidden" />
          <span id="button-text">{{ $t('checkout.placeOrder')}}</span>
        </button>
        <div id="payment-message" class="hidden" />
      </form>
    </div>
  </div>
</template>
<script>
import { get } from "@/utils/lodash";
import paymentApiReq from "@/api/payment";
import goTo from "../../../mixins/goTo";
import { mapState, mapActions } from "vuex";
import { reportError } from "@/utils/error-reporter";
import {
  storeOrderSnapshot,
  readOrderSnapshot,
  clearOrderSnapshot,
  notifyOrderPlaced,
} from "@/utils/order-notifier";

export default {
  mixins: [goTo],
  props: {
    loadCheckout: {
      type: Boolean,
      default: false,
    },
    triggerConfirmPayment: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Number,
      default: 0,
    },
    shoppingCartId: {
      type: Number,
      default: 0,
    },
    calculationData: {
      type: Object,
      default: () => ({}),
    },
    deliveryBranch: {
      type: Object,
      default: () => null,
    }
  },
  data() {
    return {
      isPaymentVisible: false,
      isPreparingPayment: false,
      isPaymentConfirmationVisible: false,
      stripe: null,
      elements: null,
      paymentId: null,
      clientSecret: null,
      clientKey: null,
      totalAmount: null,
      isLoading: false,
      isAuthorizing: false,
      countryCode: localStorage.getItem('countryCode'),
    };
  },
  computed: {
    ...mapState("user", ["user", "userDetails"]),

    // Identifies the current cart calculation. Used to key a pending PaymentIntent so
    // a reload/re-entry reuses the same intent instead of creating (and paying) a new one.
    cartSignature() {
      return get(this.calculationData, "signature", null);
    },
  },
  watch: {
    loadCheckout(newVal) {
      if (newVal) {
        this.initializePayment();
        this.checkPaymentStatus();
      }
    },
    triggerConfirmPayment(newVal) {
      if (newVal) {
        this.confirmPayment();
      }
    },
  },
  mounted() {
    const stripeScript = document.createElement("script");
    stripeScript.src = "https://js.stripe.com/v3/";
    
    stripeScript.onload = () => {
      this.stripe = this.countryCode == "QC" ? window.Stripe(`${window.processEnv.VUE_APP_STRIPE_PUBLIC_KEY}`) : window.Stripe(`${window.processEnv.VUE_APP_STRIPE_PUBLIC_KEY_ONTARIO}`);
      this.$emit("stripe-load-end", true);
    };
    
    document.head.appendChild(stripeScript);
  },
  methods: {
    ...mapActions("user", ["updateUser"]),
    ...mapActions("shoppingCart", ["updateShoppingCart"]),
    async initializePayment() {
      // Guard against duplicate authorizations. The loadCheckout watcher can fire more
      // than once (re-entering the payment step, a page reload, a province switch), and
      // each authorize() mints a brand-new PaymentIntent that Portaus pays from the Stripe
      // webhook the moment it succeeds — i.e. a duplicate charge. Reuse any existing intent
      // for this cart before creating another.
      if (this.isAuthorizing) {
        return;
      }
      this.isAuthorizing = true;
      this.isPreparingPayment = true;
      try {
        const reused = await this.reuseExistingIntent();
        if (reused) {
          return;
        }
        await this.authorizePayment();
      } finally {
        this.isAuthorizing = false;
        this.isPreparingPayment = false;
      }
    },
    // Reuse a PaymentIntent already created for this cart, if one exists. Returns true
    // when the existing intent was reused (either finalized because it already succeeded,
    // or re-shown because it's still open), false when a fresh intent should be created.
    async reuseExistingIntent() {
      const existingSecret = this.storedIntentSecret();
      if (!existingSecret) {
        return false;
      }
      try {
        const { paymentIntent } = await this.stripe.retrievePaymentIntent(existingSecret);
        const status = get(paymentIntent, "status");
        if (status === "succeeded" || status === "processing") {
          // Already paid (or settling) for this exact cart — finalize instead of
          // charging again. This is the key duplicate-charge guard across reloads.
          this.finalizePayment(paymentIntent);
          return true;
        }
        if (
          status === "requires_payment_method" ||
          status === "requires_confirmation" ||
          status === "requires_action"
        ) {
          // Unpaid intent still open for this cart — reuse it so a retry doesn't create
          // a second intent.
          this.showExistingElement(existingSecret);
          return true;
        }
        // canceled / unexpected — drop it and let a fresh intent be created.
        this.clearStoredIntent();
        return false;
      } catch (retrieveErr) {
        reportError("STRIPE_INTENT_REUSE_FAILED", {
          message: retrieveErr && retrieveErr.message,
          error: retrieveErr,
        });
        return false;
      }
    },
    authorizePayment() {
      const isGuestUser = localStorage.getItem("isGuestUser") === 'true';

      var customerData = {};
      var portausCompany = 1;
      if(!isGuestUser){
        customerData = {
            firstName: this.user.firstName,
            lastName: this.user.lastName,
            email: this.user.email,
            phone: this.user.phone,
            billingAddress: this.user.billingAddress,
          }
          // Send a copy of billing rather than omitting the key when delivery and
          // billing match. Omitting it (a379ea4) leaves the SalesOrder with no
          // delivery address at all, so the order desk sees a blank field even on
          // orders where nothing was lost — which is most of them, since the
          // "same as billing" box is ticked by default.
          //
          // This restores the shape prod ran until 2026-05-20 and that Portaus has
          // always accepted. The omit shape came from Alex's spec for
          // PUT /customers/{id}, a different endpoint with a different contract;
          // that one still omits (delivery-address-form.vue).
          customerData.shippingAddress =
            this.user.shippingAddress &&
            JSON.stringify(this.user.shippingAddress) !== JSON.stringify(this.user.billingAddress)
              ? this.user.shippingAddress
              : this.user.billingAddress;
          portausCompany = this.userDetails.portausCompany.id;
      }else{
          const guestData = JSON.parse(localStorage.getItem("guestUserDetails") || "{}");

          const billingAddress = this.buildGuestAddress(
            guestData.rue,
            guestData.ville,
            guestData.codePostal
          );

          customerData = {
            firstName: guestData.firstName,
            lastName: guestData.lastName,
            email: guestData.email,
            phone: guestData.phone,
            billingAddress,
          };
          // Always a non-null object — see the logged-in branch below.
          customerData.shippingAddress = guestData.copyAddress
            ? billingAddress
            : this.buildGuestAddress(
                guestData.rue2,
                guestData.ville2,
                guestData.codePostal2
              );
          portausCompany = this.countryCode == 'QC' ? 1 : 2;
      }
      // Mirror of what Portaus is about to be sent. Kept so the order desk can be
      // told exactly this once the payment succeeds — Portaus currently loses the
      // address and phone off web orders (open backend bug, see order-notifier.js).
      storeOrderSnapshot({
        market: this.countryCode,
        portausCompanyId: portausCompany,
        isGuest: isGuestUser,
        customer: customerData,
        deliveryBranch: this.deliveryBranch || null,
        lines: (this.calculationData.lines || []).slice(0, 60).map((l) => ({
          puid: get(l, "product.puid", null),
          name: get(l, "product.name", null) || get(l, "name", null),
          qty: get(l, "qty", null),
        })),
        total: this.calculationData.totalBillable,
      });

      return paymentApiReq
        .authorize(
          {
            customer: customerData,
            portausCompanyId: portausCompany,
            lines: this.calculationData.lines,
            signature: this.calculationData.signature,
            subTotal: this.calculationData.subTotal,
            subTotalBillable: this.calculationData.subTotalBillable,
            taxes: this.calculationData.taxes,
            total: this.calculationData.total,
            totalBillable: this.calculationData.totalBillable,
            totalUnbillable: this.calculationData.totalUnbillable,
            ...(this.deliveryBranch && { deliveryBranch: this.deliveryBranch }),
          },
          {}
        )
        .then((result) => {
          /* eslint-disable object-curly-newline */
          const clientSecret = result.detail?.intentPayment?.client_secret;
          this.storeIntent(clientSecret);
          this.mountPaymentElement(clientSecret);
        })
        .catch((error) => {
          const ref = reportError("STRIPE_AUTHORIZE_FAILED", {
            message: error && error.message,
            error,
          });
          this.$emit(
            "error",
            `${this.$i18n.t('checkout.errorMessage2')} (${this.$i18n.t('checkout.errorReference', { ref })})`
          );
        });
    },
    // Mount the Stripe Payment Element for a given client secret. Reuses the already-mounted
    // element when it's for the same intent so re-entry doesn't double-mount.
    mountPaymentElement(clientSecret) {
      if (this.elements && this.clientSecret === clientSecret) {
        this.isPaymentVisible = true;
        return;
      }
      this.clientSecret = clientSecret;
      this.elements = this.stripe.elements({
        appearance: {
          theme: "stripe",
        },
        clientSecret,
        fields: {
          billingDetails: {
            name: "auto",
          },
        },
      });
      const paymentElement = this.elements.create("payment");
      paymentElement.mount("#payment-element");
      this.isPaymentVisible = true;
    },
    showExistingElement(clientSecret) {
      this.mountPaymentElement(clientSecret);
    },
    // Persist the pending intent keyed by the cart signature so a reload/re-entry can
    // reuse it instead of creating a new one.
    // Builds a guest address in the shape Portaus expects.
    //
    // Logged-in customers get province and country for free: their addresses come
    // back from Portaus already shaped, and the customer-update PUT rebuilds them
    // the same way (delivery-address-form.vue). Guests had neither — this built
    // street/city/postalCode only, so Portaus received an address with no province
    // and no country.
    //
    // That is exactly what Alex traced on 2026-07-30 in the "Commande du site web"
    // thread: "lorsque le client n'existe pas, l'adresse s'inscris mal (sans pays
    // et province)". He patched around it server-side; this stops sending the
    // malformed shape in the first place.
    //
    // Ids match delivery-address-form.vue: province 1 = QC, 2 = ON, country 1 =
    // Canada (its hardcoded `defaultCountries`). The site sells in Canada only.
    buildGuestAddress(street, city, postalCode) {
      return {
        street,
        city,
        postalCode,
        state: { id: this.countryCode === "ON" ? 2 : 1 },
        country: { id: 1 },
      };
    },
    // Mirrors the paid order to the order desk. Best-effort and never awaited:
    // the payment has already gone through, so nothing here may delay or fail
    // the customer's confirmation. The server de-duplicates on the intent id.
    notifyOrderDesk() {
      try {
        const snapshot = readOrderSnapshot();
        const intentId = this.paymentId;
        if (!snapshot || !intentId) return;
        notifyOrderPlaced(snapshot, intentId, {
          total: snapshot.total,
        });
        clearOrderSnapshot();
      } catch (e) {
        // Never let the notification break the confirmation step.
      }
    },
    storeIntent(clientSecret) {
      const signature = this.cartSignature;
      if (!signature || !clientSecret) {
        return;
      }
      try {
        localStorage.setItem(
          "pendingPaymentIntent",
          JSON.stringify({ signature, clientSecret })
        );
      } catch (e) {
        // localStorage unavailable (private mode/quota) — fall back to in-memory only.
      }
    },
    clearStoredIntent() {
      try {
        localStorage.removeItem("pendingPaymentIntent");
      } catch (e) {
        // ignore
      }
    },
    // Returns the client secret of a pending intent for the current cart, if any —
    // preferring the in-memory one, then a stored one whose signature still matches.
    storedIntentSecret() {
      if (this.clientSecret) {
        return this.clientSecret;
      }
      try {
        const raw = localStorage.getItem("pendingPaymentIntent");
        if (!raw) {
          return null;
        }
        const parsed = JSON.parse(raw);
        if (parsed && parsed.signature && parsed.signature === this.cartSignature) {
          return parsed.clientSecret;
        }
      } catch (e) {
        // ignore malformed/unavailable storage
      }
      return null;
    },
    confirmPayment() {
      // Stripe success is authoritative: per Portaus' documented design, Portaus creates
      // and pays the SalesOrder + invoice from the Stripe webhook the moment the
      // PaymentIntent succeeds. There is no front-end finalize step — the old
      // /payments/intents/confirm call was a vestige of a removed user-driven "Confirmer
      // la commande" flow, did nothing the webhook doesn't already do, and 404s on the
      // test2 backend where the route no longer exists. Just finalize the UI.
      this.notifyOrderDesk();
      this.showMessage("Payment successful!");
      this.createEmptyCart();
      this.goTo("/checkout/confirmation");
    },
    async handleSubmit(e) {
      e.preventDefault();
      this.setLoading(true);

      try {
        const response = await this.stripe.confirmPayment({
          elements: this.elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: "if_required",
        });

        if (response.error) {
          const errorType = get(response, "error.type");

          // card_error / validation_error are genuine pre-charge declines — no money
          // moved, so it's safe to surface the message and let the customer retry.
          if (errorType === "card_error" || errorType === "validation_error") {
            this.showMessage(response.error.message);
            this.setLoading(false);
            return;
          }

          // Any other error (api/connection/unknown) is ambiguous: the PaymentIntent
          // may have succeeded server-side even though we never got a clean response.
          // Portaus creates and pays the SalesOrder from the Stripe webhook on success,
          // so verify the real status before telling the customer it failed — otherwise
          // they retry and get charged again on a brand-new intent.
          const ref = reportError("STRIPE_UNHANDLED_ERROR", {
            message: response.error && response.error.message,
            data: {
              type: response.error && response.error.type,
              code: response.error && response.error.code,
            },
          });
          const finalized = await this.finalizeIfPaid();
          if (!finalized) {
            this.showMessage(`An unexpected error occurred. (${this.$i18n.t('checkout.errorReference', { ref })})`);
          }
          this.setLoading(false);
          return;
        }

        const paymentIntent = response.paymentIntent;
        const status = get(paymentIntent, "status");

        // "processing" settles asynchronously and the webhook finalizes the order on
        // success, so treat it like "succeeded" rather than pushing a retry.
        if (status === "succeeded" || status === "processing") {
          this.finalizePayment(paymentIntent);
        } else {
          // Missing/unknown status from confirmPayment — verify against Stripe before
          // failing, since the charge may already have gone through.
          const finalized = await this.finalizeIfPaid();
          if (!finalized) {
            this.showMessage("Payment not completed. Please try again.");
          }
        }
      } catch (err) {
        // confirmPayment threw (e.g. network error). The charge may still have
        // gone through server-side, so verify before telling the customer it failed.
        const ref = reportError("STRIPE_CONFIRM_THREW", {
          message: err && err.message,
          error: err,
        });
        const finalized = await this.finalizeIfPaid();
        if (!finalized) {
          this.showMessage(`Payment processing failed. (${this.$i18n.t('checkout.errorReference', { ref })})`);
        }
      }

      this.setLoading(false);
    },

    // Mark the payment as done and hand off to the confirmation step. Safe to drive
    // from a freshly-confirmed PaymentIntent or one retrieved from Stripe after an
    // ambiguous error.
    finalizePayment(paymentIntent) {
      this.clientKey = paymentIntent.client_secret;
      this.paymentId = paymentIntent.id;
      this.totalAmount = paymentIntent.amount;
      this.isPaymentVisible = false;
      // The order is now paid/settling — drop the pending intent so a later genuine
      // order doesn't reuse it.
      this.clearStoredIntent();
      this.$emit("enable-confirmation", true);
      this.showMessage("Payment successful!");
    },

    // Ask Stripe for the authoritative status of the current PaymentIntent. If it
    // already succeeded (or is processing toward success), finalize the order so a
    // transient frontend error doesn't make the customer retry and pay twice.
    // Returns whether the payment was treated as final.
    async finalizeIfPaid() {
      if (!this.clientSecret || !this.stripe) {
        return false;
      }
      try {
        const { paymentIntent } = await this.stripe.retrievePaymentIntent(
          this.clientSecret
        );
        const status = get(paymentIntent, "status");
        if (status === "succeeded" || status === "processing") {
          this.finalizePayment(paymentIntent);
          return true;
        }
      } catch (retrieveErr) {
        reportError("STRIPE_INTENT_RETRIEVE_FAILED", {
          message: retrieveErr && retrieveErr.message,
          error: retrieveErr,
        });
      }
      return false;
    },

    checkPaymentStatus() {
      if (!this.clientKey) {
        return;
      }
      this.stripe.retrievePaymentIntent(this.clientKey).then(({ paymentIntent }) => {
        if (!paymentIntent) {
          return;
        }
        switch (paymentIntent.status) {
          case "succeeded":
            this.showMessage("Payment succeeded!");
            break;
          case "processing":
            this.showMessage("Your payment is processing.");
            break;
          case "requires_payment_method":
            this.showMessage(
              "Your payment was not successful, please try again."
            );
            break;
          default:
            this.showMessage("Something went wrong.");
            break;
        }
      });
    },
    showMessage(messageText) {
      const messageContainer = document.querySelector("#payment-message");

      messageContainer.classList.remove("hidden");
      messageContainer.textContent = messageText;

      setTimeout(() => {
        messageContainer.classList.add("hidden");
        messageContainer.textContent = "";
      }, 4000);
    },
    setLoading(isLoading) {
      this.isLoading = isLoading;
      if (isLoading) {
        // Disable the button and show a spinner
        document.querySelector("#submit").disabled = true;
        document.querySelector("#spinner").classList.remove("hidden");
        document.querySelector("#button-text").classList.add("hidden");
      } else {
        document.querySelector("#submit").disabled = false;
        document.querySelector("#spinner").classList.add("hidden");
        document.querySelector("#button-text").classList.remove("hidden");
      }
    },
    createEmptyCart(){
      if (localStorage.getItem('access-token')) {
        const uId = JSON.parse(localStorage.getItem("user"));
        const cartId = localStorage.getItem("x-cart");
        this.updateShoppingCart({
            id: uId?.id || cartId,
            shoppingCart: {},
          })
          .then(() => {
            console.log('Cart is empty');
          })
          .catch(() => {
            console.log('Cart is not empty');
          });
      }else{
        this.$store.dispatch('shoppingCart/emptyShoppingCart');
        localStorage.removeItem('cartProductsQC');
        localStorage.removeItem('cartProductsON');
      }
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/checkout.scss";
</style>
